import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useAuth } from '../providers/AuthContext';
import { useSettings } from '../providers/SettingsContext';
import { useRpcRouter } from './use-rpc-router.hook';
// Query key functions
function buildIdentityPreferencesQueryKey(identityId, applicationName) {
    return ['identity-preferences', identityId, applicationName];
}
export const useIdentityPreferences = ({ applicationName }) => {
    const { identityId, loggedIn } = useAuth();
    const { apiUrl } = useSettings();
    const router = useRpcRouter(apiUrl);
    const queryClient = useQueryClient();
    // Query for preferences
    const preferencesQuery = useQuery({
        queryKey: buildIdentityPreferencesQueryKey(identityId, applicationName),
        queryFn: async () => {
            const preferencesData = await router.common.getApplicationPreferencesForMyIdentity.execute({
                applicationName
            });
            return new Map(Object.entries(preferencesData));
        },
        enabled: !!identityId && loggedIn
    });
    // Mutation for updating preferences
    const updatePreferencesMutation = useMutation({
        mutationFn: async (newPreferences) => {
            await router.common.updateApplicationPreferencesForMyIdentity.execute({
                applicationName,
                preferences: newPreferences
            });
            return newPreferences;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: buildIdentityPreferencesQueryKey(identityId, applicationName)
            });
        }
    });
    // Manual refresh function
    const refreshMyApplicationPreferences = useCallback(async () => {
        if (!loggedIn) {
            return;
        }
        await queryClient.refetchQueries({
            queryKey: buildIdentityPreferencesQueryKey(identityId, applicationName)
        });
    }, [loggedIn, queryClient, identityId, applicationName]);
    // Get single preference value
    const getValue = useCallback((key) => {
        if (key == null) {
            return undefined;
        }
        return preferencesQuery.data?.get(key);
    }, [preferencesQuery.data]);
    // Update preferences with optimistic updates
    const updateValues = useCallback(async (newPreferences) => {
        // Optimistic update
        const currentData = preferencesQuery.data ?? new Map();
        const optimisticData = new Map([...currentData, ...Object.entries(newPreferences)]);
        queryClient.setQueryData(buildIdentityPreferencesQueryKey(identityId, applicationName), optimisticData);
        try {
            await updatePreferencesMutation.mutateAsync(newPreferences);
        }
        catch (error) {
            console.error('Failed to update identity preferences:', error);
            // Revert optimistic update
            queryClient.setQueryData(buildIdentityPreferencesQueryKey(identityId, applicationName), currentData);
            throw error;
        }
    }, [preferencesQuery.data, queryClient, identityId, applicationName, updatePreferencesMutation]);
    return {
        preferences: preferencesQuery.data ?? new Map(),
        getValue,
        updateValues,
        refreshMyApplicationPreferences,
        isLoading: preferencesQuery.isLoading,
        isError: preferencesQuery.isError,
        error: preferencesQuery.error
    };
};
export const useIdentityPreferenceValue = ({ key, applicationName, defaultValue }) => {
    const { identityId, loggedIn } = useAuth();
    const { apiUrl } = useSettings();
    const router = useRpcRouter(apiUrl);
    const queryClient = useQueryClient();
    // Query for preferences
    const preferencesQuery = useQuery({
        queryKey: buildIdentityPreferencesQueryKey(identityId, applicationName),
        queryFn: async () => {
            const preferencesData = await router.common.getApplicationPreferencesForMyIdentity.execute({
                applicationName
            });
            return new Map(Object.entries(preferencesData));
        },
        enabled: !!identityId && loggedIn
    });
    // Mutation for updating preferences
    const updatePreferencesMutation = useMutation({
        mutationFn: async (newPreferences) => {
            await router.common.updateApplicationPreferencesForMyIdentity.execute({
                applicationName,
                preferences: newPreferences
            });
            return newPreferences;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: buildIdentityPreferencesQueryKey(identityId, applicationName)
            });
        }
    });
    // Get current value from preferences
    const currentValue = useMemo(() => {
        return preferencesQuery.data?.get(key) ?? defaultValue;
    }, [preferencesQuery.data, key, defaultValue]);
    // Update single preference value
    const updateValue = useCallback(async (newValue) => {
        if (key == null) {
            return;
        }
        // Optimistic update
        const currentData = preferencesQuery.data ?? new Map();
        const optimisticData = new Map(currentData);
        if (newValue !== undefined) {
            optimisticData.set(key, newValue);
        }
        else {
            optimisticData.delete(key);
        }
        queryClient.setQueryData(buildIdentityPreferencesQueryKey(identityId, applicationName), optimisticData);
        try {
            await updatePreferencesMutation.mutateAsync({ [key]: newValue });
        }
        catch (error) {
            console.error('Failed to update identity preference value:', error);
            // Revert optimistic update
            queryClient.setQueryData(buildIdentityPreferencesQueryKey(identityId, applicationName), currentData);
            throw error;
        }
    }, [key, preferencesQuery.data, queryClient, identityId, applicationName, updatePreferencesMutation]);
    return [currentValue, updateValue];
};
//# sourceMappingURL=use-identity-preferences.hook.js.map